import { useEffect, useRef, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Dialog } from '@opendoor/atoms/dialog';
import { Text } from '@opendoor/atoms/text';
import { formatCurrency } from '@opendoor/shared-fe/formatUtils';
import { IconHelp } from '@tabler/icons-react';
import { redactGraphqlRequestErrorForTelemetry } from 'components/api/graphqlRequestError';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import { MORTGAGE_APP_URL } from 'components/globals';
import { captureError } from 'helpers/captureError';

import type { MarketplaceProperty } from '../types';
import { CreateListingMortgageApplicationLinkDocument } from './__generated__/mortgage-application.zeus';
import { PdpRaisedButton } from './PdpRaisedButton';

import styles from './PdpMortgageEstimate.module.css';

type MortgageOffer = NonNullable<MarketplaceProperty['mortgageOffer']>;
export type MortgageQuoteRefresh = {
  offer: MortgageOffer | null;
  message: string;
  canRetryQuote: boolean;
};
const PRODUCT_LABELS = { FIVE_YEAR_ARM: '5/6 ARM', THIRTY_YEAR_FIXED: '30-year fixed' };
const GENERIC_APPLICATION_URL = `${MORTGAGE_APP_URL}/new-application?utm_source=opendoor-com&skip_landing_page=true`;

export const PdpMortgageEstimate = ({
  addressId,
  offer,
  estimate,
  onQuoteRefresh,
  useGenericApplication = false,
}: {
  addressId: string;
  offer: MortgageOffer;
  estimate: MortgageOffer['products'][number];
  onQuoteRefresh: (quote: MortgageQuoteRefresh) => void;
  useGenericApplication?: boolean;
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [openingApplication, setOpeningApplication] = useState(false);
  const [applicationError, setApplicationError] = useState<string>();
  const idempotencyKey = useRef<string | null>(null);
  const applicationRequest = useRef<AbortController | null>(null);
  useEffect(() => {
    const restore = (event: PageTransitionEvent) => {
      if (event.persisted) setOpeningApplication(false);
    };
    window.addEventListener('pageshow', restore);
    return () => {
      window.removeEventListener('pageshow', restore);
      applicationRequest.current?.abort();
    };
  }, []);
  const openDetails = () => setDetailsOpen(true);
  const openApplication = async () => {
    const controller = new AbortController();
    applicationRequest.current = controller;
    setOpeningApplication(true);
    setApplicationError(undefined);
    idempotencyKey.current ??= crypto.randomUUID();
    try {
      const { buyerMortgageApplicationLinkCreate: result } = await requestZeusFromBrowser(
        CreateListingMortgageApplicationLinkDocument,
        {
          input: {
            addressId,
            quoteId: offer.quoteId,
            idempotencyKey: idempotencyKey.current,
          },
        },
        controller.signal,
      );
      if (result.__typename === 'MortgageApplicationLink') {
        window.location.assign(result.url);
        return;
      }
      if (result.__typename === 'MortgageQuoteUnavailableError') {
        if (
          !result.canRetryQuote &&
          result.currentOffer?.quoteId === offer.quoteId &&
          result.currentOffer.products.some((product) => product.product === 'THIRTY_YEAR_FIXED')
        ) {
          window.location.assign(GENERIC_APPLICATION_URL);
          return;
        }
        onQuoteRefresh({
          offer: result.currentOffer,
          message: result.message,
          canRetryQuote: result.canRetryQuote,
        });
      } else {
        captureError(
          new Error(`Mortgage preapproval returned ${result.__typename}`),
          'MarketplaceMortgagePreapproval',
        );
        setApplicationError('We couldn’t open pre-approval. Please try again.');
      }
    } catch (error) {
      if (controller.signal.aborted) return;
      captureError(redactGraphqlRequestErrorForTelemetry(error), 'MarketplaceMortgagePreapproval');
      setApplicationError('We couldn’t open pre-approval. Please try again.');
    }
    setOpeningApplication(false);
  };
  return (
    <Col className="w-full gap-4">
      <Row className="w-full items-center gap-6">
        <Col className="min-w-0 flex-1 gap-3">
          <Text
            aria-label="Estimated monthly principal and interest"
            className="flex min-h-5 items-center text-body-sm text-od-content-secondary"
          >
            Est. monthly payment
          </Text>
          <Text className="whitespace-nowrap text-subheader-sm @[18rem]:text-header-sm text-od-content-primary">
            {formatCurrency(estimate.principalAndInterestCents / 100)}{' '}
            <span className="text-label-md @[18rem]:text-label-xl">/ mo</span>
          </Text>
        </Col>
        <Col className="min-w-0 flex-1 gap-3">
          <Text className="flex min-h-5 items-center whitespace-nowrap text-body-sm text-od-content-secondary">
            {estimate.isPromotionalRate ? 'Special rate' : 'Rate'}
          </Text>
          <Text className="text-subheader-sm @[18rem]:text-header-sm text-od-content-primary">
            <span className={estimate.isPromotionalRate ? styles.specialRate : undefined}>
              {estimate.ratePercent}%
            </span>
          </Text>
        </Col>
      </Row>
      <Row
        onClick={openDetails}
        analyticsName="cosmos-marketplace-mortgage-details-open"
        aria-label={`${estimate.aprPercent}% APR · ${PRODUCT_LABELS[estimate.product]} — View mortgage estimate details`}
        aria-haspopup="dialog"
        className="relative min-h-5 w-fit self-start cursor-pointer flex-wrap items-center gap-1 border-0 bg-transparent p-0 text-left text-body-sm text-od-content-secondary focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-4 focus-visible:outline-od-border-focused-secondary after:absolute after:inset-x-0 after:-inset-y-3 after:content-[''] [-webkit-tap-highlight-color:transparent]"
      >
        <Text as="span" className="text-body-sm text-od-content-secondary">
          {estimate.aprPercent}% APR · {PRODUCT_LABELS[estimate.product]}
        </Text>
        <IconHelp size={16} stroke={2} aria-hidden />
      </Row>
      <PdpRaisedButton
        aria-label="Get pre-approved for free"
        analyticsName="cosmos-marketplace-mortgage-preapproval"
        {...(useGenericApplication
          ? {
              href: GENERIC_APPLICATION_URL,
            }
          : { onClick: openApplication, loading: openingApplication })}
        variant="primary"
        size="md"
        className="h-12 w-full px-3 text-label-sm"
      >
        Get pre-approved for free
      </PdpRaisedButton>
      {applicationError && (
        <Text role="alert" className="text-body-sm text-od-content-error">
          {applicationError}
        </Text>
      )}
      <Dialog.Root role="dialog" open={detailsOpen} onOpenChange={setDetailsOpen}>
        <Dialog className="flex max-h-[85dvh] flex-col gap-6 p-6 sm:w-[560px] sm:p-8">
          <Dialog.Title className="pr-10 text-subheader-sm sm:text-subheader-md">
            Mortgage estimate details
          </Dialog.Title>
          <Col className="min-h-0 gap-5 overflow-y-auto text-body-sm text-od-content-secondary">
            <Dialog.Description className="text-body-md text-od-content-secondary">
              {PRODUCT_LABELS[estimate.product]} at {estimate.ratePercent}% interest (
              {estimate.aprPercent}% APR). The initial monthly estimate includes principal and
              interest only. Taxes, insurance, and HOA dues are additional.
            </Dialog.Description>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 border-y border-od-border-primary py-5">
              <dt>Purchase price</dt>
              <dd className="text-right text-label-sm text-od-content-primary">
                {formatCurrency(offer.purchasePriceCents / 100)}
              </dd>
              <dt>Down payment</dt>
              <dd className="text-right text-label-sm text-od-content-primary">
                {offer.downPaymentPercent}%
              </dd>
              <dt>Loan amount</dt>
              <dd className="text-right text-label-sm text-od-content-primary">
                {formatCurrency(offer.loanAmountCents / 100)}
              </dd>
              <dt>Credit score</dt>
              <dd className="text-right text-label-sm text-od-content-primary">
                {offer.creditScore}
              </dd>
              <dt>Rate lock</dt>
              <dd className="text-right text-label-sm text-od-content-primary">
                {offer.lockPeriodDays} days
              </dd>
              <dt>Borrower-paid discount points</dt>
              <dd className="text-right text-label-sm text-od-content-primary">None</dd>
            </dl>
            {estimate.product === 'FIVE_YEAR_ARM' && (
              <Text as="p" className="text-body-sm text-od-content-secondary">
                The rate is fixed for the first five years of a 30-year loan, then may adjust;
                payments may increase.
              </Text>
            )}
            <Text as="p" className="text-body-sm text-od-content-secondary">
              Rates as of{' '}
              {new Date(offer.rateAsOf).toLocaleDateString('en-US', { timeZone: 'UTC' })}. Rates and
              eligibility are subject to change. Pre-approval starts an application and does not
              lock this rate.
            </Text>
            <Text as="p" className="text-body-sm text-od-content-secondary">
              Cash pricing is a separate purchase option and is not used for this estimate.
            </Text>
          </Col>
          <Dialog.Close
            render={(props) => (
              <Button
                {...props}
                aria-label="Close mortgage details"
                analyticsName="cosmos-marketplace-mortgage-details-close"
                variant="secondary"
                className="h-12 w-full shrink-0 rounded-xl text-label-sm"
              >
                Close
              </Button>
            )}
          />
        </Dialog>
      </Dialog.Root>
    </Col>
  );
};
